import { gql, useLazyQuery } from '@apollo/client';
import Select from '@orn/shared-web/components/Select';
import { UserIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import type { AdminListUsersQuery, AdminListUsersQueryVariables } from './__generated__/AdminSelectUser.gql';

const ADMIN_LIST_USERS = gql`
  query AdminListUsers {
    adminListUsers {
      id
      email
      firstName
      lastName
    }
  }
`;

interface IAdminSelectUserProps {
  selectedUserId: string | null;
  onSelectionChange: (userId: string | null) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  hideLabel?: boolean;
}

const AdminSelectUser = ({
  selectedUserId,
  onSelectionChange,
  label = 'Select User',
  placeholder = 'Select a user...',
  className,
  hideLabel = false,
}: IAdminSelectUserProps) => {
  const [loadUsers, { data: usersData, loading: usersLoading }] = useLazyQuery<
    AdminListUsersQuery,
    AdminListUsersQueryVariables
  >(ADMIN_LIST_USERS);

  // Load users on mount
  React.useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const selectItems = React.useMemo(() => {
    const users = usersData?.adminListUsers ?? [];
    if (users.length === 0) {
      return [{ id: 'no-users-found', label: 'No items found', isDisabled: true }];
    }

    return users.map((user) => ({
      id: user.id,
      label: user.email,
      supportingText:
        user.firstName || user.lastName ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : undefined,
    }));
  }, [usersData?.adminListUsers]);

  if (usersLoading) {
    return <div className="text-secondary text-sm">Loading users...</div>;
  }

  return (
    <Select
      label={hideLabel ? undefined : label}
      placeholder={placeholder}
      value={selectedUserId || null}
      onChange={(key) => {
        if (key) {
          onSelectionChange(key as string);
        } else {
          onSelectionChange(null);
        }
      }}
      items={selectItems}
      className={className}
    >
      {(item) => (
        <Select.Item
          id={item.id}
          supportingText={item.supportingText}
          icon={item.isDisabled ? undefined : UserIcon}
          isDisabled={item.isDisabled}
        >
          {item.label}
        </Select.Item>
      )}
    </Select>
  );
};

export default AdminSelectUser;
